
import React from "react";
import { Link } from "react-router-dom";
import { Puzzle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ToolUsageStats } from "@/hooks/useToolUsageStats";

interface ToolCardProps {
  toolId: string;
  name: string;
  description: string;
  configPath: string;
  stats?: ToolUsageStats;
  craftCount?: number;
  sceneCount?: number;
  recipeCount?: number;
}

const ToolCard: React.FC<ToolCardProps> = ({ 
  toolId, 
  name, 
  description, 
  configPath, 
  stats,
  craftCount,
  sceneCount,
  recipeCount
}) => {
  // Only show loading indicator for tools that have database content to count
  const hasCountsToLoad = toolId === 'craftGenerator' || toolId === 'sceneGenerator' || toolId === 'recipeGenerator';
  const isLoading = hasCountsToLoad && craftCount === undefined && sceneCount === undefined && recipeCount === undefined;
  
  return (
    <div className="border rounded-lg p-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Puzzle className="h-5 w-5 text-elfGreen" />
            {name}
          </h3>
          <p className="text-gray-600 mt-1">
            {description}
          </p>
          
          <div className="mt-3 flex flex-wrap gap-3">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Status:</span> <span className="text-green-600">Active</span>
            </div>
            {stats && (
              <>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Embedded:</span> {stats.postEmbeds || 0} posts
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Used:</span> {stats.totalUsage || 0} times
                </div>
              </>
            )}
            {isLoading ? (
              <div className="text-sm text-gray-600 flex items-center">
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                Loading content counts...
              </div>
            ) : (
              <>
                {craftCount !== undefined && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Crafts:</span> {craftCount} curated
                  </div>
                )}
                {sceneCount !== undefined && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Scenes:</span> {sceneCount} curated
                  </div>
                )}
                {recipeCount !== undefined && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Recipes:</span> {recipeCount} curated
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <Link to={configPath}>
            <Button className="bg-elfGreen hover:bg-elfGreen-dark">
              Configure
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ToolCard;
